import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Chip, Paper, Stack, Typography } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { getChapterSlides } from '../data/chapterSlides';

function WorkedExampleSlide({ slide }) {
  const [stepIndex, setStepIndex] = useState(0);
  const steps = slide.steps || [];
  const step = steps[stepIndex] || {};
  const statusColor = step.status === 'feasible' ? '#159a63' : step.status === 'too-high' || step.status === 'too-low' ? '#c83d54' : '#2f63ff';

  const isActiveNumber = (value) => value >= step.left && value <= step.right;
  const getNumberState = (value) => {
    if (value === step.mid) return 'mid';
    if (slide.kind === 'number-line' && value * value <= 26 && step.status !== 'unknown') return 'feasible';
    if (slide.kind === 'number-line' && value * value > 26 && step.status !== 'unknown') return 'invalid';
    if (slide.kind === 'koko' && step.mid && value >= step.mid && step.status === 'feasible') return 'feasible';
    if (slide.kind === 'koko' && step.mid && value <= step.mid && step.status === 'too-low') return 'invalid';
    return 'neutral';
  };

  const stateStyles = {
    mid: { bgcolor: '#2f63ff', color: '#fff', borderColor: '#2f63ff', boxShadow: '0 10px 24px rgba(47,99,255,0.28)' },
    feasible: { bgcolor: 'rgba(21,154,99,0.12)', color: '#0f6f48', borderColor: 'rgba(21,154,99,0.3)' },
    invalid: { bgcolor: 'rgba(200,61,84,0.1)', color: '#9a2638', borderColor: 'rgba(200,61,84,0.26)' },
    neutral: { bgcolor: 'rgba(255,255,255,0.72)', color: '#0a0d14', borderColor: 'rgba(10,13,20,0.08)' },
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
        <Box>
          <Typography sx={{ fontWeight: 900, color: '#0a0d14', lineHeight: 1.2 }}>{slide.subject}</Typography>
          <Typography sx={{ color: 'rgba(10,13,20,0.58)', fontSize: '0.82rem', fontWeight: 750 }}>
            Step {stepIndex + 1} of {steps.length}
          </Typography>
        </Box>
        <Chip
          label={step.status === 'done' ? `answer ${slide.finalAnswer}` : step.mid ? `mid ${step.mid}` : 'setup'}
          sx={{ bgcolor: statusColor, color: '#fff', fontWeight: 850 }}
        />
      </Stack>

      {slide.kind === 'koko' && (
        <Stack direction="row" spacing={1} alignItems="end" sx={{ minHeight: 116 }}>
          {(slide.piles || []).map((pile) => (
            <Box key={pile} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  height: `${44 + pile * 4}px`,
                  minHeight: 54,
                  borderRadius: '18px 18px 8px 8px',
                  bgcolor: 'rgba(47,99,255,0.14)',
                  border: '1px solid rgba(47,99,255,0.24)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#174bd6',
                  fontWeight: 900,
                }}
              >
                {pile}
              </Box>
              <Typography sx={{ mt: 0.6, color: 'rgba(10,13,20,0.58)', fontSize: '0.72rem', fontWeight: 800 }}>
                pile
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {slide.kind === 'concept' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 1,
          }}
        >
          {steps.map((item, index) => {
            const active = index === stepIndex;
            const done = index < stepIndex;
            return (
              <Box
                key={`${item.label}-${index}`}
                sx={{
                  minHeight: 92,
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: active ? '#0a0d14' : done ? 'rgba(21,154,99,0.12)' : 'rgba(255,255,255,0.72)',
                  color: active ? '#fff' : '#0a0d14',
                  border: '1px solid',
                  borderColor: active ? '#0a0d14' : done ? 'rgba(21,154,99,0.26)' : 'rgba(10,13,20,0.08)',
                  boxShadow: active ? '0 14px 34px rgba(10,13,20,0.18)' : 'none',
                  overflow: 'hidden',
                }}
              >
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, opacity: 0.62 }}>
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={{ fontWeight: 900, lineHeight: 1.18, mt: 0.7, fontSize: '0.86rem' }}>
                  {item.state || item.label}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.7,
                    opacity: active ? 0.76 : 0.58,
                    fontSize: '0.72rem',
                    fontFamily: item.value?.startsWith?.('{') || item.value?.startsWith?.('[') ? '"SFMono-Regular", Consolas, monospace' : 'inherit',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {slide.kind !== 'concept' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${slide.kind === 'koko' ? 11 : 14}, minmax(0, 1fr))`,
            gap: 0.55,
            alignItems: 'center',
          }}
        >
          {(slide.values || slide.speeds || []).map((value) => {
            const state = getNumberState(value);
            return (
              <Box
                key={value}
                sx={{
                  ...stateStyles[state],
                  opacity: isActiveNumber(value) || state !== 'neutral' ? 1 : 0.34,
                  aspectRatio: '1 / 1',
                  minWidth: 0,
                  borderRadius: 1.4,
                  border: '1px solid',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 900,
                  fontSize: { xs: '0.68rem', sm: '0.78rem' },
                  transition: '180ms ease',
                }}
              >
                {value}
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.76)', border: '1px solid rgba(10,13,20,0.08)' }}>
        <Typography sx={{ color: '#0a0d14', fontWeight: 900 }}>{step.label}</Typography>
        <Typography sx={{ mt: 0.7, color: 'rgba(10,13,20,0.68)', lineHeight: 1.45, fontSize: '0.88rem' }}>
          {step.note}
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="outlined"
          onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
          disabled={stepIndex === 0}
          sx={{ bgcolor: 'rgba(255,255,255,0.58)' }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => setStepIndex((current) => Math.min(steps.length - 1, current + 1))}
          disabled={stepIndex === steps.length - 1}
        >
          Advance step
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {(slide.legend || []).map((item) => (
          <Chip key={item} label={item} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: '#0a0d14', fontWeight: 750 }} />
        ))}
      </Stack>
    </Stack>
  );
}

function SlideBody({ slide }) {
  if (slide.type === 'worked-example') {
    return <WorkedExampleSlide slide={slide} />;
  }

  if (slide.type === 'diagram') {
    if (slide.imageSrc) {
      return (
        <Box>
          <Box
            component="img"
            src={slide.imageSrc}
            alt={`${slide.title} visual`}
            loading="lazy"
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              objectFit: 'cover',
              display: 'block',
              borderRadius: 3,
              border: '1px solid rgba(10,13,20,0.08)',
              boxShadow: '0 18px 50px rgba(82,107,170,0.16)',
              bgcolor: 'rgba(255,255,255,0.68)',
            }}
          />
          {slide.bullets?.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.4, flexWrap: 'wrap', rowGap: 1 }}>
              {slide.bullets.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: '#0a0d14', fontWeight: 750 }}
                />
              ))}
            </Stack>
          )}
        </Box>
      );
    }

    const items = slide.diagram || slide.bullets || [];
    return (
      <Box sx={{ position: 'relative', minHeight: 240 }}>
        <Box
          sx={{
            position: 'absolute',
            inset: '46% 12% auto 12%',
            height: 2,
            bgcolor: 'rgba(10,13,20,0.16)',
          }}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems="stretch"
          justifyContent="space-between"
          sx={{ position: 'relative' }}
        >
          {items.map((item, index) => (
            <Box
              key={item}
              sx={{
                flex: 1,
                minHeight: 118,
                p: 1.8,
                borderRadius: 3,
                bgcolor: index === 1 ? '#0a0d14' : 'rgba(255,255,255,0.72)',
                color: index === 1 ? '#fff' : '#0a0d14',
                border: '1px solid rgba(10,13,20,0.08)',
                boxShadow: index === 1 ? '0 18px 50px rgba(10,13,20,0.2)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, opacity: 0.68 }}>
                STEP {index + 1}
              </Typography>
              <Typography sx={{ fontWeight: 850, lineHeight: 1.22, mt: 2 }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Typography sx={{ color: 'rgba(10,13,20,0.58)', lineHeight: 1.55, mt: 2.2, fontSize: '0.9rem' }}>
          Diagram generated from the chapter invariant: identify the signal, preserve the state, then move the boundary safely.
        </Typography>
      </Box>
    );
  }

  if (slide.type === 'code') {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 2.5,
          bgcolor: '#0a0d14',
          color: '#eaf0ff',
          overflowX: 'auto',
          fontSize: { xs: '0.72rem', md: '0.82rem' },
          lineHeight: 1.55,
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
          boxShadow: '0 18px 50px rgba(10,13,20,0.2)',
        }}
      >
        <code>{slide.code}</code>
      </Box>
    );
  }

  if (slide.type === 'table') {
    return (
      <Stack spacing={1}>
        {(slide.rows || []).map(([state, reason], index) => (
          <Box
            key={`${state}-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '0.8fr 1.2fr' },
              gap: 1.2,
              p: 1.4,
              borderRadius: 2,
              bgcolor: index % 2 === 0 ? 'rgba(255,255,255,0.68)' : 'rgba(255,255,255,0.42)',
              border: '1px solid rgba(10,13,20,0.07)',
            }}
          >
            <Typography sx={{ color: '#0a0d14', fontWeight: 850, fontSize: '0.9rem' }}>{state}</Typography>
            <Typography sx={{ color: 'rgba(10,13,20,0.68)', lineHeight: 1.5, fontSize: '0.9rem' }}>
              {reason}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  if (slide.type === 'problem') {
    return (
      <Stack spacing={1.4}>
        {(slide.bullets || []).map((bullet) => (
          <Box
            key={bullet}
            sx={{
              p: 1.4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.62)',
              border: '1px solid rgba(10,13,20,0.07)',
            }}
          >
            <Typography sx={{ color: '#0a0d14', fontWeight: 750 }}>{bullet}</Typography>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.2}>
      {(slide.bullets || []).map((bullet, index) => (
        <Stack direction="row" spacing={1.4} alignItems="flex-start" key={bullet}>
          <Box
            sx={{
              width: 26,
              height: 26,
              flex: '0 0 auto',
              borderRadius: '50%',
              bgcolor: '#0a0d14',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: '0.75rem',
              fontWeight: 850,
            }}
          >
            {index + 1}
          </Box>
          <Typography sx={{ color: '#0a0d14', lineHeight: 1.5, fontWeight: 650 }}>
            {bullet}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export default function ChapterSlideshow({ chapter }) {
  const slides = useMemo(() => getChapterSlides(chapter), [chapter]);
  const [active, setActive] = useState(0);
  const slide = slides[active];
  const goPrevious = () => setActive((current) => (current === 0 ? slides.length - 1 : current - 1));
  const goNext = () => setActive((current) => (current === slides.length - 1 ? 0 : current + 1));

  if (!slides.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        mb: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          minHeight: { xs: 340, md: 430 },
          p: { xs: 2.5, md: 4 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
          background: slide.background,
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            pointerEvents: 'none',
            inset: '12% 8% auto auto',
            width: { xs: 150, md: 260 },
            aspectRatio: '1 / 1',
            borderRadius: '44% 56% 62% 38%',
            background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.96), rgba(94,118,255,0.22) 54%, rgba(66,184,255,0.34))',
            filter: 'blur(0.2px)',
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            pointerEvents: 'none',
            inset: 'auto 0 0 auto',
            width: '52%',
            height: '42%',
            background: 'radial-gradient(circle at 50% 50%, rgba(58,91,255,0.24), transparent 68%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', maxWidth: 560 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label="Visual lesson"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.72)', color: 'rgba(10,13,20,0.72)' }}
            />
            <Typography sx={{ color: 'rgba(10,13,20,0.52)', fontSize: '0.82rem', fontWeight: 800 }}>
              {active + 1} / {slides.length}
            </Typography>
          </Stack>
          <Typography variant="h4" sx={{ color: '#0a0d14', fontWeight: 900, letterSpacing: 0, mb: 1 }}>
            {slide.title}
          </Typography>
          <Typography sx={{ color: 'rgba(10,13,20,0.68)', fontSize: '1rem', lineHeight: 1.6 }}>
            {slide.caption}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <Button variant="outlined" startIcon={<NavigateBeforeIcon />} onClick={goPrevious} sx={{ bgcolor: 'rgba(255,255,255,0.58)' }}>
              Previous
            </Button>
            <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={goNext}>
              Next
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            position: 'relative',
            p: { xs: 2, md: 3 },
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.62)',
            border: '1px solid rgba(10,13,20,0.08)',
            boxShadow: '0 20px 70px rgba(82,107,170,0.16)',
            backdropFilter: 'blur(18px)',
          }}
        >
          <Typography sx={{ color: 'rgba(10,13,20,0.54)', fontSize: '0.78rem', fontWeight: 850, mb: 1.5 }}>
            {slide.type === 'code' ? 'SOLUTION' : slide.type === 'table' ? 'DRY RUN' : slide.type === 'problem' ? 'PROBLEM SETUP' : slide.type === 'diagram' ? 'DIAGRAM' : slide.type === 'worked-example' ? 'INTERACTIVE TRACE' : 'KEY IDEAS'}
          </Typography>
          <SlideBody slide={slide} />
        </Box>
      </Box>
      <Stack direction="row" spacing={1} sx={{ p: 1.5 }} aria-label="Slideshow navigation">
        {slides.map((item, index) => (
          <ButtonBase
            key={item.title}
            onClick={() => setActive(index)}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 999,
              bgcolor: index === active ? 'primary.main' : 'action.hover',
            }}
            aria-label={`Show ${item.title}`}
          />
        ))}
      </Stack>
    </Paper>
  );
}
